import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import type { GetState, SetState } from "stores/types";
import type { Project, ProjectStore } from "./projectStore";
import type { Node } from "interfaces/schemas";
import { getLanguage } from "stores/globalStore";
import {
  createNode,
  updateNode,
  updateNodeConfiguration,
  deleteNode,
  moveNode,
  pasteNode,
  copyNode,
  toggleNodeActiveStatus,
  pasteWizardNode,
  type MoveNodeParams,
} from "./actions";
import { useProjectUIStore, useClipboardStore } from "stores/project";
import * as treeUtils from "utils/treeUtils";
import type { ProductOutput } from "stores/product";

export type ProjectActions = {
  createNode: (node: NodeApi<Node>) => void;
  deleteNode: (node: NodeApi<Node>) => void;
  updateNode: (node: NodeApi<Node>, args: Partial<Node>) => void;
  updateNodeConfiguration: ({
    nodeId,
    productOutput,
    callback,
  }: {
    nodeId: string;
    productOutput: ProductOutput;
    callback?: () => void;
  }) => void;
  moveNode: (args: MoveNodeParams) => void;
  copyNode: (node: NodeApi<Node>) => void;

  pasteNode: (node: NodeApi<Node>, callback?: (node: Node) => void) => void;
  duplicateNode: (node: NodeApi<Node>, callback?: () => void) => void;
  toggleNodeActiveStatus: (node: NodeApi<Node>) => void;
};

export const createProjectActions = (
  set: SetState<ProjectStore>,
  get: GetState<ProjectStore>,
): ProjectActions => ({
  createNode: (node) =>
    runAction(
      ({ language, tree, project }) =>
        createNode(node, tree, project, language),
      set,
      get,
    ),
  deleteNode: (node) =>
    runAction(
      ({ language, tree, project }) =>
        deleteNode(node, tree, project, language),
      set,
      get,
    ),
  updateNode: (node, args) =>
    runAction(
      ({ language, tree, project }) =>
        updateNode(node, args, tree, project, language),
      set,
      get,
    ),
  moveNode: (args) =>
    runAction(
      ({ language, tree, project }) => {
        const setRedrawIndex =
          useProjectUIStore.getState().actions.setRedrawIndex;
        return moveNode(args, tree, project, language, setRedrawIndex);
      },
      set,
      get,
    ),
  copyNode: (node) => {
    const setCopiedItem = useClipboardStore.getState().setCopiedItem;
    runAction(() => copyNode(node, setCopiedItem), set, get);
  },
  pasteNode: (parent, callback) => {
    const copiedItem = useClipboardStore.getState().copiedItem;

    if (!copiedItem || !treeUtils.isWizardRootFolder(copiedItem.data)) {
      runAction(
        ({ language, tree, project }) =>
          pasteNode(
            copiedItem,
            parent,
            tree,
            project,
            language,
            null,
            callback,
          ),
        set,
        get,
      );
      return;
    }
    runAction(
      ({ language, tree, project }) =>
        pasteWizardNode(copiedItem, parent, tree, project, language, callback),
      set,
      get,
    );
  },
  duplicateNode: (node, callback) =>
    runAction(
      ({ language, tree, project }) =>
        pasteNode(node, node.parent, tree, project, language, null, callback),
      set,
      get,
    ),
  toggleNodeActiveStatus: (node) =>
    runAction(
      ({ language, tree, project }) =>
        toggleNodeActiveStatus(node, tree, project, language),
      set,
      get,
    ),
  updateNodeConfiguration: ({ nodeId, productOutput, callback }) =>
    runAction(
      ({ language, tree, project }) =>
        updateNodeConfiguration(
          nodeId,
          productOutput,
          tree,
          project,
          language,
          callback,
        ),
      set,
      get,
    ),
});

const runAction = async (
  actionFn: ({
    language,
    tree,
    project,
  }: {
    language: string;
    tree: Node[];
    project: Project;
  }) => Node[] | null | Promise<Node[] | null>,
  set: SetState<ProjectStore>,
  get: GetState<ProjectStore>,
) => {
  try {
    // Get the current project, tree, and language from the store
    const project = get().project;
    const tree = get().tree;
    const language = getLanguage();
    if (!project) {
      throw new Error("Project is not initialized");
    }
    if (!tree) {
      throw new Error("Project tree is not initialized");
    }
    if (!language) {
      throw new Error("Language is not set");
    }

    // Call the action function with the current state
    const newTree = await actionFn({ language, tree, project });
    // If the action function returns a new tree, update the store with the new tree
    if (newTree) {
      set({ tree: newTree });
    }
  } catch (error) {
    console.error("An error occurred whilst performing an action:", error);
  }
};
